import { logWarn } from 'lib/logging'

export type MilestoneCardKind = `closed-won` | `closed-lost` | `journey`

/**
 * Journey beats rendered by JourneyMilestoneCard. Descriptions must match the
 * backend timeline projection exactly
 * (docs/superpowers/specs/2026-08-24-milestone-workflow-design.md).
 */
export const JOURNEY_MILESTONE_DESCRIPTIONS = [
  `Became Lead`,
  `First Appointment Scheduled`,
  `Went Inactive`,
  `Reactivated`,
] as const

/** One-way journey beats: the timeline shows each at most once per contact
 *  (docs/superpowers/specs/2026-08-24-milestone-workflow-design.md). */
export const ONE_WAY_MILESTONE_DESCRIPTIONS = [`Became Lead`, `First Appointment Scheduled`, `Closed Won`] as const

type MilestoneLike = {
  type?: string
  description?: string
  activityDate?: string
  createdDate?: string
}

const isOneWayMilestone = (activity: MilestoneLike) =>
  activity.type === `Milestone` &&
  !!activity.description &&
  (ONE_WAY_MILESTONE_DESCRIPTIONS as readonly string[]).includes(activity.description)

// Missing dates lose ties so a dated original always beats an undated stray.
const occurrenceOrder = (activity: MilestoneLike) =>
  `${activity.activityDate ?? `9999`}|${activity.createdDate ?? `9999`}`

/**
 * The write side can hold duplicate one-way milestones (legacy docs plus
 * import races) until the backend projection's dedup sweep lands (TRK-5584).
 * The timeline enforces the spec invariant at render instead: at most one card
 * per one-way beat, keeping the earliest occurrence. Loop beats (Closed Lost,
 * Went Inactive, Reactivated) are never collapsed.
 */
export function dedupeOneWayMilestones<T extends MilestoneLike>(activities: T[]): T[] {
  const kept = new Map<string, T>()
  for (const activity of activities) {
    if (!isOneWayMilestone(activity)) continue
    const current = kept.get(activity.description!)
    if (!current || occurrenceOrder(activity) < occurrenceOrder(current)) kept.set(activity.description!, activity)
  }
  if (kept.size === 0) return activities
  return activities.filter((activity) => !isOneWayMilestone(activity) || kept.get(activity.description!) === activity)
}

export function milestoneCardKind(description: string | undefined): MilestoneCardKind {
  if (description === `Closed Won`) return `closed-won`
  if (description && (JOURNEY_MILESTONE_DESCRIPTIONS as readonly string[]).includes(description)) return `journey`
  // Closed Lost and any legacy/unknown description keep today's fallback rendering,
  // but an unknown description means the backend projection drifted from this catalog.
  if (description !== `Closed Lost`) {
    logWarn(`unknown milestone description, rendering as Closed Lost`, description)
  }
  return `closed-lost`
}
